import React, { useState, useEffect } from 'react';
import { serverAnalytics } from './services/serverAnalytics';
import TopNavigation from './TopNavigation';
import ClueRaiderBanner from './components/ClueRaiderBanner';
import { updatePageSEO } from './utils/seo';

interface DailyStat {
  date: string;
  totalVisits: number;
  uniqueVisitors: number;
  botVisits?: number;
  pageViews: Record<string, number>;
}

interface AnalyticsData {
  stats: DailyStat[];
  totals: {
    totalVisits: number;
    totalUniqueVisitors: number;
    totalBotVisits?: number;
    averageDailyVisits: number;
    averageDailyUnique: number;
  };
  topPages: Array<{ page: string; count: number }>;
  lastUpdated: string;
}

const AdminAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState('');
  const [days, setDays] = useState('30');
  const [showTokenInput, setShowTokenInput] = useState(true);

  useEffect(() => {
    updatePageSEO(
      'Admin Analytics - Dimension Flux',
      'View server-side analytics for Dimension Flux'
    );
  }, []);

  const loadAnalytics = async () => {
    console.log('loadAnalytics called', { hasToken: !!authToken, tokenLength: authToken?.length, days });
    
    if (!authToken) {
      console.warn('No auth token provided');
      setError('Please enter an analytics token');
      return;
    }

    console.log('Setting loading to true');
    setLoading(true);
    setError(null);
    console.log('Loading analytics with token:', authToken.substring(0, 10) + '...', 'days:', days);

    try {
      console.log('Calling serverAnalytics.getAnalytics...');
      const startTime = Date.now();
      // Convert days to number if it's a string like "today" or "yesterday"
      const daysParam = days === 'today' ? 'today' : days === 'yesterday' ? 'yesterday' : parseInt(days, 10);
      const data = await serverAnalytics.getAnalytics(daysParam, authToken);
      const duration = Date.now() - startTime;
      console.log(`Received analytics data in ${duration}ms:`, data);
      setAnalytics(data);
      setShowTokenInput(false);
      setLoading(false);
    } catch (err: any) {
      console.error('Error in loadAnalytics:', err);
      console.error('Error stack:', err.stack);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      
      let errorMessage = err.message || 'Failed to load analytics';
      
      // Provide helpful error message if function isn't available
      if (errorMessage.includes('Function not available') || 
          errorMessage.includes('<!DOCTYPE') ||
          errorMessage.includes('netlify dev')) {
        errorMessage = 'Netlify functions are not available. Please run `netlify dev` instead of `npm start` to test the analytics dashboard.';
      } else if (errorMessage.includes('does not exist') || errorMessage.includes('42P01')) {
        errorMessage = 'Analytics tables do not exist in Supabase. Please run the SQL setup from supabase_setup.sql in your Supabase SQL Editor.';
      } else if (errorMessage.includes('Supabase not configured')) {
        errorMessage = 'Supabase environment variables are not set. Check your .env file has SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY.';
      } else if (errorMessage.includes('timeout') || err.name === 'AbortError') {
        errorMessage = 'Request timed out. The function may be taking too long or there may be a connection issue.';
      }
      
      console.error('Setting error message:', errorMessage);
      setError(errorMessage);
      setShowTokenInput(true);
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  if (showTokenInput || !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <TopNavigation />
        <ClueRaiderBanner />
        <div className="max-w-4xl mx-auto p-4 mt-8">
          <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Admin Analytics</h1>
            <p className="text-gray-700 mb-4">
              Enter your analytics authentication token to view server-side analytics data.
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (authToken) {
                  await loadAnalytics();
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Analytics Token
                </label>
                <input
                  type="password"
                  value={authToken}
                  onChange={(e) => setAuthToken(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                  placeholder="Enter token"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days to Show
                </label>
                <select
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="7">Last 7 days</option>
                  <option value="14">Last 14 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="60">Last 60 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={!authToken}
                className="w-full bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Load Analytics
              </button>
              {loading && (
                <div className="text-sm text-gray-600 text-center">
                  Loading analytics data...
                </div>
              )}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <TopNavigation />
      <ClueRaiderBanner />
      <div className="max-w-6xl mx-auto p-4 mt-8">
        {/* Header */}
        <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Admin Analytics</h1>
            <button
              onClick={() => {
                setShowTokenInput(true);
                setAnalytics(null);
              }}
              className="text-sm bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300"
            >
              Change Token
            </button>
          </div>
          <div className="flex gap-4 mb-4">
            <select
              value={days}
              onChange={(e) => {
                setDays(e.target.value);
                loadAnalytics();
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            >
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <button
              onClick={loadAnalytics}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
            >
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-600">
            Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">Total Visits</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(analytics.totals.totalVisits)}
            </div>
          </div>
          <div className="bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">Unique Visitors</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(analytics.totals.totalUniqueVisitors)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (Real users only)
            </div>
          </div>
          <div className="bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">Bot Visits</div>
            <div className="text-2xl font-bold text-orange-600">
              {formatNumber(analytics.totals.totalBotVisits || 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (Filtered out)
            </div>
          </div>
          <div className="bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">Avg Daily Visits</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(analytics.totals.averageDailyVisits)}
            </div>
          </div>
          <div className="bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">Avg Daily Unique</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(analytics.totals.averageDailyUnique)}
            </div>
          </div>
        </div>

        {/* Daily Stats Table */}
        <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Daily Statistics</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-2 px-3 text-gray-700 font-semibold">Date</th>
                  <th className="text-right py-2 px-3 text-gray-700 font-semibold">Total Visits</th>
                  <th className="text-right py-2 px-3 text-gray-700 font-semibold">Unique Visitors</th>
                  <th className="text-right py-2 px-3 text-gray-700 font-semibold">Bot Visits</th>
                  <th className="text-right py-2 px-3 text-gray-700 font-semibold">Real User %</th>
                </tr>
              </thead>
              <tbody>
                {analytics.stats.map((stat, index) => {
                  const realUserVisits = stat.totalVisits - (stat.botVisits || 0);
                  const realUserPercentage = stat.totalVisits > 0
                    ? ((realUserVisits / stat.totalVisits) * 100).toFixed(1)
                    : '0';
                  return (
                    <tr
                      key={stat.date}
                      className={index % 2 === 0 ? 'bg-gray-50' : ''}
                    >
                      <td className="py-2 px-3 text-gray-900">{formatDate(stat.date)}</td>
                      <td className="py-2 px-3 text-right text-gray-900">
                        {formatNumber(stat.totalVisits)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900">
                        {formatNumber(stat.uniqueVisitors)}
                      </td>
                      <td className="py-2 px-3 text-right text-orange-600">
                        {formatNumber(stat.botVisits || 0)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600">
                        {realUserPercentage}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Top Pages</h2>
          <div className="space-y-2">
            {analytics.topPages.map((page, index) => (
              <div
                key={page.page}
                className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded"
              >
                <span className="text-gray-900 font-mono text-sm">{page.page}</span>
                <span className="text-gray-700 font-semibold">{formatNumber(page.count)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">Note:</p>
          <p className="mb-2">
            This data is collected server-side and complements GA4. It may be more accurate
            for users who block cookies or use privacy-focused browsers. Unique visitor counts
            are estimated using browser fingerprinting and may not be 100% accurate.
          </p>
          <p className="font-semibold mt-2 mb-1">Bot Detection:</p>
          <p>
            Bot visits are automatically detected and filtered out from unique visitor counts.
            Common crawlers (Googlebot, Bingbot, etc.), monitoring tools, and automated scripts
            are identified by their user agent strings. Only real user visits are counted in
            the "Unique Visitors" metric.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;

